import { useState, useCallback, useEffect, useMemo } from "react"
import dayjs from "dayjs"
import { Button, Checkbox, Flex, Form, Tooltip } from "antd"
import { CloseOutlined, DeleteOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons"
import { useTranslation, Trans } from "react-i18next"
import { useSelector } from "react-redux"
import { modelSelector } from "store/selectors"
import { Card } from "ui/Card"
import { Text } from "ui/Typography"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { NewConfirmModal } from "ui/NewConfirmModal"
import { ScheduleExperienceModal } from "../ScheduleExperienceModal"
import { useWizardContext } from "../../../WizardContext"
import styles from "./TicketedSchedule.module.css"
import { spacings } from "themes/variables"

const VISIBLE_LIMIT = 3

export const TicketedSchedule = () => {
  const { t } = useTranslation()
  const { form } = useWizardContext()
  const trip = useSelector(modelSelector("trip"))

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)
  const [timeSlots, setTimeSlots] = useState(() => form.getFieldValue("time_slots") || [])
  const [selectedKeys, setSelectedKeys] = useState([])

  // Sync local state when WizardProvider populates the form asynchronously after trip loads.
  // The useState initializer above reads the form at mount time — before setFieldsValue runs —
  // so timeSlots starts as [] and never updates without this watch.
  const timeSlotsWatch = Form.useWatch("time_slots", form)
  const durationValue = Form.useWatch("duration_value", form)
  const durationUnit = Form.useWatch("duration_unit", form)
  // duration_type / number_of_days are registered on the ticket type step, which is
  // unmounted here — useWatch skips unregistered fields unless preserve is set.
  const durationType = Form.useWatch("duration_type", { form, preserve: true })
  const numberOfDays = Form.useWatch("number_of_days", { form, preserve: true })
  const titleWatch = Form.useWatch("title", form)
  useEffect(() => {
    if (timeSlots.length === 0 && timeSlotsWatch?.length > 0) {
      setTimeSlots(timeSlotsWatch)
    }
  }, [timeSlotsWatch])

  // WizardProvider initializes the form once — if time_slots arrive after that initial run
  // (stale Redux cache race), the form watch never updates. Read directly from Redux as a fallback.
  // Skipped after a deliberate clear (duration type switch) — Redux keeps the old slots until
  // the scheduling step saves, and re-seeding them here would undo the clear.
  useEffect(() => {
    if (form.getFieldValue("is_schedule_cleared")) return
    if (timeSlots.length > 0 || !trip?.time_slots?.length) return
    const slots = trip.time_slots.map((slot) => ({
      ...slot,
      label: slot.label || dayjs(slot.starts_at).format("ddd MMM D, YY h:mm A")
    }))
    setAndSync(slots)
  }, [trip?.time_slots])
  const [isShowingAll, setIsShowingAll] = useState(false)

  // Writes to both local state (re-render) and form store (persistence) in one step.
  const setAndSync = (updater) => {
    setTimeSlots((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater
      form.setFieldValue("time_slots", next)
      return next
    })
  }

  const visibleSlots = isShowingAll ? timeSlots : timeSlots.slice(0, VISIBLE_LIMIT)
  const hiddenCount = timeSlots.length - VISIBLE_LIMIT
  const isAllSelected = timeSlots.length > 0 && selectedKeys.length === timeSlots.length

  const handleSelectAll = (e) => {
    setSelectedKeys(e.target.checked ? timeSlots.map((s) => s.id) : [])
  }

  const handleSelectOne = (id, checked) => {
    setSelectedKeys((prev) => (checked ? [...prev, id] : prev.filter((k) => k !== id)))
  }

  const handleDeleteRequest = (id = null) => {
    setPendingDeleteId(id)
    setIsDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (pendingDeleteId !== null) {
      setAndSync((prev) => prev.filter((slot) => slot.id !== pendingDeleteId))
      setSelectedKeys((prev) => prev.filter((key) => key !== pendingDeleteId))
      setPendingDeleteId(null)
    } else {
      setAndSync((prev) => prev.filter((slot) => !selectedKeys.includes(slot.id)))
      setSelectedKeys([])
    }
    setIsDeleteConfirmOpen(false)
  }

  const handleDeleteCancel = () => {
    setPendingDeleteId(null)
    setIsDeleteConfirmOpen(false)
  }

  // Returns { added, skipped } so the modal can show inline feedback and stay open.
  const handleConfirm = useCallback((newSlots) => {
    const existingLabels = new Set(timeSlots.map((slot) => slot.label))
    const unique = newSlots.filter((slot) => !existingLabels.has(slot.label))
    const skipped = newSlots.length - unique.length

    if (unique.length > 0) {
      setAndSync((prev) => [...prev, ...unique])
      setIsShowingAll(false)
    }
    return { added: unique.length, skipped }
  }, [timeSlots])

  // Read duration from form fields (user may have changed them without saving)
  // Fall back to trip data if form hasn't been populated yet.
  // Multi-day experiences have no duration inputs on this tab — the duration
  // comes from number_of_days selected on an earlier step.
  const computedDurationMinutes = useMemo(() => {
    if (durationType === "multi_day") {
      return numberOfDays ? numberOfDays * 24 * 60 : null
    }
    if (durationValue && (durationUnit === "minute" || durationUnit === "hour")) {
      return durationUnit === "hour" ? durationValue * 60 : durationValue
    }
    return null
  }, [durationType, numberOfDays, durationValue, durationUnit])

  const experienceName = titleWatch ?? trip?.title ?? ""
  const hasDuration = computedDurationMinutes !== null

  return (
    <>
      <Card
        className="mb-xl"
        title={
          <Flex align="center" gap={6}>
            {t("experiences.ticketed_schedule.title")} *
            <Tooltip placement="right" title={t("experiences.ticketed_schedule.title_tooltip")}>
              <InfoCircleOutlined className={styles.infoIcon} />
            </Tooltip>
          </Flex>
        }
        extra={
          <Button size="large" type="primary" shape="round" icon={<PlusOutlined />} disabled={!hasDuration} onClick={() => setIsModalOpen(true)}>
            {t("experiences.ticketed_schedule.schedule_button")}
          </Button>
        }
      >
        <WithEmptyStateWrapper
          name="schedule"
          hasItems={timeSlots.length > 0}
          fallbackTitle={t("experiences.ticketed_schedule.empty_state")}
        >
          <Flex vertical gap={4}>
            {selectedKeys.length > 0 && (
              <Flex align="center" justify="space-between" className={styles.bulkBar}>
                <Text className={styles.bulkCount}>{t("experiences.ticketed_schedule.selected_count", { count: selectedKeys.length })}</Text>
                <Flex align="center" gap={spacings.XS}>
                  <Button danger type="primary" onClick={() => handleDeleteRequest()}>
                    {t("global.delete")}
                  </Button>
                  <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setSelectedKeys([])} />
                </Flex>
              </Flex>
            )}
            <Flex align="center" gap={4} className={styles.startTimesHeader}>
              <Checkbox checked={isAllSelected} onChange={handleSelectAll} />
              <Text className={styles.startTimesLabel}>{t("experiences.ticketed_schedule.start_times")}</Text>
            </Flex>

            {visibleSlots.map((slot) => {
              const isSelected = selectedKeys.includes(slot.id)
              return (
                <Flex
                  key={slot.id}
                  align="center"
                  gap={8}
                  className={`${styles.slotRow} ${isSelected ? styles.slotRowSelected : ""}`}
                >
                  <Checkbox checked={isSelected} onChange={(e) => handleSelectOne(slot.id, e.target.checked)} />
                  <Text className={styles.slotText}>{slot.label}</Text>
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteRequest(slot.id)}
                    className={styles.deleteBtn}
                  />
                </Flex>
              )
            })}

            {!isShowingAll && hiddenCount > 0 && (
              <Button type="link" onClick={() => setIsShowingAll(true)} className={styles.showMoreBtn}>
                {t("experiences.ticketed_schedule.show_more", { count: hiddenCount })}
              </Button>
            )}
          </Flex>
        </WithEmptyStateWrapper>
      </Card>

      <ScheduleExperienceModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirm}
        experienceName={experienceName}
        durationMinutes={computedDurationMinutes}
        isMultiDay={durationType === "multi_day"}
      />

      <NewConfirmModal
        visible={isDeleteConfirmOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("experiences.ticketed_schedule.delete_confirm.title")}
        message={
          <Trans
            i18nKey="experiences.ticketed_schedule.delete_confirm.message"
            values={{ count: pendingDeleteId === null ? selectedKeys.length : 1 }}
            components={{ bold: <strong /> }}
          />
        }
        confirmLabel={t("global.delete")}
      />
    </>
  )
}
